import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock, Send, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/quote-requests`, formData);
      setSubmitted(true);
      toast.success('Your quote request has been submitted successfully!');
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Address',
      content: 'Silversquare, 523 Avenue Louise\n1050 Bruxelles, Belgique',
    },
    {
      icon: Phone,
      title: 'Phone',
      content: '+32 496 79 64 61',
      href: 'tel:+32496796461',
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'jemimabolokwa@supply-hub.eu',
      href: 'mailto:jemimabolokwa@supply-hub.eu',
    },
    {
      icon: Clock,
      title: 'Business Hours',
      content: 'Monday - Friday\n9:00 AM - 6:00 PM CET',
    },
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative bg-[#0F172A] pt-32 pb-20 noise-overlay">
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-[#0EA5E9] text-sm font-medium uppercase tracking-widest">
              Contact Us
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              Let's Discuss Your Procurement Needs
            </h1>
            <p className="text-slate-400 text-lg leading-relaxed">
              Ready to optimize your supply chain? Get in touch with our team for 
              a free consultation and quote.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section data-testid="contact-form-section" className="bg-[#F8FAFC] py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="bg-white border border-slate-200 p-8 md:p-12">
              <h2 className="font-['Outfit'] text-2xl md:text-3xl font-semibold text-[#0F172A] mb-2">
                Request a Quotation
              </h2>
              <p className="text-slate-600 mb-8">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>

              {submitted ? (
                <div data-testid="success-message" className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="text-green-600" size={32} />
                  </div>
                  <h3 className="font-['Outfit'] text-xl font-semibold text-[#0F172A] mb-2">
                    Thank You!
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Your request has been received. Our team will contact you shortly.
                  </p>
                  <Button
                    onClick={() => setSubmitted(false)}
                    className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-none px-6 py-3"
                  >
                    Submit Another Request
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-[#0F172A] font-medium">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="input-name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        required
                        className="bg-slate-50 border-slate-200 rounded-none h-12 focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-[#0F172A] font-medium">
                        Company *
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        data-testid="input-company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your Company Ltd."
                        required
                        className="bg-slate-50 border-slate-200 rounded-none h-12 focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#0F172A] font-medium">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="input-email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@company.com"
                        required
                        className="bg-slate-50 border-slate-200 rounded-none h-12 focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-[#0F172A] font-medium">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        data-testid="input-phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+32 xxx xxx xxx"
                        className="bg-slate-50 border-slate-200 rounded-none h-12 focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-[#0F172A] font-medium">
                      Your Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="input-message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Describe your procurement needs, products required, quantities, and any specific requirements..."
                      required
                      rows={6}
                      className="bg-slate-50 border-slate-200 rounded-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="submit-quote-btn"
                    disabled={isSubmitting}
                    className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white rounded-none py-4 font-medium text-base transition-colors duration-300"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={18} />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2" size={18} />
                        Request a Quotation
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="font-['Outfit'] text-2xl md:text-3xl font-semibold text-[#0F172A] mb-8">
                Get in Touch
              </h2>

              <div className="space-y-6 mb-12">
                {contactInfo.map((item, index) => (
                  <div
                    key={item.title}
                    data-testid={`contact-info-${index}`}
                    className="flex gap-4"
                  >
                    <div className="w-12 h-12 bg-[#0F172A] flex items-center justify-center flex-shrink-0">
                      <item.icon className="text-white" size={20} />
                    </div>
                    <div>
                      <h3 className="font-medium text-[#0F172A] mb-1">{item.title}</h3>
                      {item.href ? (
                        <a
                          href={item.href}
                          className="text-slate-600 hover:text-[#0EA5E9] transition-colors whitespace-pre-line"
                        >
                          {item.content}
                        </a>
                      ) : (
                        <p className="text-slate-600 whitespace-pre-line">{item.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Map Placeholder */}
              <div className="h-[300px] relative overflow-hidden">
                <img
                  src="/5.jpeg"
                  alt="Global Business Travel"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Additional Info */}
              <div className="mt-8 bg-[#0F172A] p-6">
                <h3 className="font-['Outfit'] text-lg font-semibold text-white mb-3">
                  Why Contact Us?
                </h3>
                <ul className="space-y-2">
                  {[
                    'Free consultation and needs assessment',
                    'Detailed quotation within 24-48 hours',
                    'No obligation to proceed',
                    'Expert advice on procurement strategies',
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-2 text-slate-300 text-sm">
                      <CheckCircle className="text-[#0EA5E9] flex-shrink-0" size={16} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
